import { download, generateCsv, mkConfig } from "export-to-csv"
import { DateTime } from "luxon"
import { useCallback } from "react"
import { toCurrency, toDinero } from "../../../../currency/currencyLib"
import { useMoney } from "../../../../currency/useMoney"
import { useDateTime } from "../../../../localization/useDateTime"
import { Subscription } from "../../../accounting/accounting/accountingApi"

type useExportOrdersToCsvParams = Readonly<{
    data: Subscription[] | null | undefined
    startDate: DateTime
    endDate: DateTime
}>

export function useExportSubscriptionToCsv({ data, startDate, endDate }: useExportOrdersToCsvParams) {
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({ locale: "da-DK" })

    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ".",
        useBom: true,
        useKeysAsHeaders: true,
        filename: `Facilitynet-Subscription-${dateTimeFactory.formatDate(startDate)}_${dateTimeFactory.formatDate(endDate)}`,
    })

    const mapSubscriptionToCsvRow = (subscription: Subscription) => ({
        From: dateTimeFactory.formatDate(startDate),
        To: dateTimeFactory.formatDate(endDate),
        "Employee Id": subscription.subscriptionDetails?.customer?.employeeId ?? "",
        Intials: subscription.subscriptionDetails?.customer?.initials ?? "",
        Name: subscription.subscriptionDetails?.customer?.displayName ?? "",
        Email: subscription.subscriptionDetails?.customer?.email ?? "",
        "Subscription name": subscription.name ?? "",
        ["Price" +
        `(${
            subscription.subscriptionDetails?.amount
                ? moneyFactory.getLocalizedCurrencySymbol(toCurrency(subscription.subscriptionDetails.amount.currency))
                : ""
        })`]: subscription.subscriptionDetails?.amount
            ? moneyFactory.formatAsNumber(toDinero(subscription.subscriptionDetails.amount))
            : "",
        "Valid from": subscription.subscriptionDetails?.validFrom ?? "",
        "Valid to": subscription.subscriptionDetails?.validTo ?? "",
        Created: subscription.subscriptionDetails?.order?.created ?? "",
        "Customer group": subscription.subscriptionDetails?.customer?.userGroup?.name ?? "",
        Company: subscription.subscriptionDetails?.order?.accountingCustomerParty?.name ?? "",
        "Order id": subscription.subscriptionDetails?.order?.id ?? "",
        "PSP reference": "",
    })

    return useCallback(() => {
        let exportRows: ReturnType<typeof mapSubscriptionToCsvRow>[]

        if (data && data.length > 0) {
            exportRows = data.map((subscription) => mapSubscriptionToCsvRow(subscription))
        } else {
            exportRows = [mapSubscriptionToCsvRow({} as Subscription)]
        }

        const csv = generateCsv(csvOptions)(exportRows)
        download(csvOptions)(csv)
    }, [dateTimeFactory, moneyFactory, data, startDate, endDate])
}
